import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Grid,
  Avatar,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import {
  staffList,
  getUserPermissions,
  setUserPermissions,
} from "../../../Services/Services";
import { toast } from "react-toastify";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.75rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryLight,
    borderColor: T.primary,
    boxShadow: "none",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid #fecaca`,
  bgcolor: "#fef2f2",
  color: "#dc2626",
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: "#dc2626", boxShadow: "none" },
};

const SECTION_COLORS = {
  dashboard:     { bg: "#eef2ff", border: "#c7d2fe", color: "#4338ca", light: "#e0e7ff", glow: "rgba(67,56,202,0.12)" },
  settings:      { bg: "#ecfdf5", border: "#a7f3d0", color: "#059669", light: "#d1fae5", glow: "rgba(5,150,105,0.12)" },
  vehicle:       { bg: "#eff6ff", border: "#bfdbfe", color: "#2563eb", light: "#dbeafe", glow: "rgba(37,99,235,0.12)" },
  expense:       { bg: "#fffbeb", border: "#fde68a", color: "#d97706", light: "#fef3c7", glow: "rgba(217,119,6,0.12)" },
  freight:       { bg: "#fef2f2", border: "#fecaca", color: "#dc2626", light: "#fee2e2", glow: "rgba(220,38,38,0.12)" },
  billing:       { bg: "#f5f3ff", border: "#ddd6fe", color: "#7c3aed", light: "#ede9fe", glow: "rgba(124,58,237,0.12)" },
  issues:        { bg: "#fff7ed", border: "#fed7aa", color: "#ea580c", light: "#ffedd5", glow: "rgba(234,88,12,0.12)" },
  freightReport: { bg: "#fdf2f8", border: "#fbcfe8", color: "#db2777", light: "#fce7f3", glow: "rgba(219,39,119,0.12)" },
  billReport:    { bg: "#f0fdf4", border: "#bbf7d0", color: "#16a34a", light: "#dcfce7", glow: "rgba(22,163,74,0.12)" },
  landing:       { bg: "#f0f9ff", border: "#bae6fd", color: "#0284c7", light: "#e0f2fe", glow: "rgba(2,132,199,0.12)" },
};

const PAGE_SECTIONS = [
  {
    section: "Dashboard",
    sectionKey: "dashboard",
    pages: [{ key: "dashboard", label: "Dashboard" }],
  },
  {
    section: "Settings",
    sectionKey: "settings",
    pages: [
      { key: "stafflist", label: "User" },
      { key: "partylist", label: "Consignor" },
      { key: "consignee-list", label: "Consignee" },
      { key: "broker-list", label: "Broker" },
    ],
  },
  {
    section: "My Vehicle",
    sectionKey: "vehicle",
    pages: [
      { key: "VehicleList", label: "Vehicle List" },
      { key: "VehicleFrom", label: "Vehicle Form" },
      { key: "DocumentAlert", label: "Document Alert" },
    ],
  },
  {
    section: "Vehicle Expense",
    sectionKey: "expense",
    pages: [
      { key: "ExpensesForm", label: "Expenses Form" },
      { key: "ExpensesList", label: "Expenses List" },
    ],
  },
  {
    section: "LR",
    sectionKey: "freight",
    pages: [
      { key: "freight-form", label: "LR Entry" },
      { key: "freight-list", label: "LR List" },
      { key: "freightinvoice", label: "Due LR" },
      { key: "freightpayment", label: "AGS Payment" },
      { key: "blank-lr-list", label: "Blank LRs" },
    ],
  },
  {
    section: "Billing",
    sectionKey: "billing",
    pages: [
      { key: "bill-creation", label: "Bill Creation" },
      { key: "bill-list", label: "Bill List" },
      { key: "custom-bill-creation", label: "Custom Bill Creation" },
      { key: "custom-bill-list", label: "Custom Bill List" },
    ],
  },
  {
    section: "Issues",
    sectionKey: "issues",
    pages: [{ key: "issue-list", label: "Issues" }],
  },
  {
    section: "LR Reports",
    sectionKey: "freightReport",
    pages: [
      { key: "Allfreight", label: "All LR" },
      { key: "freightfilter", label: "LR Filter" },
      { key: "Billpendingfreight", label: "Bill Pending" },
      { key: "Completefreight", label: "Complete LR" },
      { key: "Partyfreight", label: "Party LR" },
      { key: "Vehiclefreight", label: "Truck LR" },
      { key: "freight/payment/report", label: "All LR Pay" },
      { key: "expense-report", label: "Expense Report" },
    ],
  },
  {
    section: "Bill Reports",
    sectionKey: "billReport",
    pages: [
      { key: "Allbills", label: "All Bills" },
      { key: "Completebill", label: "Complete Bills" },
      { key: "pendingforbill", label: "Pending Bills" },
      { key: "partyBill", label: "Party Bills" },
    ],
  },
  {
    section: "Landing Data",
    sectionKey: "landing",
    pages: [
      { key: "customer-form", label: "Customer Form" },
      { key: "customer-list", label: "Customer List" },
      { key: "testimonial-form", label: "Testimonial Form" },
      { key: "testimonial-list", label: "Testimonial List" },
      { key: "enquiry-details", label: "Enquiry Details" },
    ],
  },
];

const ALL_PAGE_KEYS = PAGE_SECTIONS.flatMap((s) => s.pages.map((p) => p.key));

const sectionAllChecked = (section, selected) =>
  section.pages.every((p) => selected.includes(p.key));

const sectionIndeterminate = (section, selected) => {
  const checked = section.pages.filter((p) => selected.includes(p.key)).length;
  return checked > 0 && checked < section.pages.length;
};

const getInitials = (name) =>
  name
    ?.split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "?";

const highlightedCheckbox = (color, isChecked) => ({
  p: 0.3,
  "& .MuiSvgIcon-root": { fontSize: 18 },
  color: isChecked ? color : "#c0c0c0",
  "&.Mui-checked": {
    color: color,
    filter: `drop-shadow(0 0 3px ${color}50)`,
  },
  "&.MuiCheckbox-indeterminate": {
    color: color,
    filter: `drop-shadow(0 0 3px ${color}50)`,
  },
  transition: "all 0.2s",
  "&:hover": {
    bgcolor: `${color}12`,
    transform: "scale(1.08)",
  },
});

const sectionCheckbox = (color, isChecked) => ({
  p: 0.3,
  "& .MuiSvgIcon-root": { fontSize: 20 },
  color: isChecked ? color : "#b0b0b0",
  "&.Mui-checked": {
    color: color,
    filter: `drop-shadow(0 0 4px ${color}60)`,
  },
  "&.MuiCheckbox-indeterminate": {
    color: color,
    filter: `drop-shadow(0 0 4px ${color}60)`,
  },
  transition: "all 0.2s",
  "&:hover": {
    bgcolor: `${color}12`,
    transform: "scale(1.1)",
  },
});

export default function UserAccessManagement() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedPages, setSelectedPages] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

 useEffect(() => {
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await staffList();
      const list = Array.isArray(data) ? data : data.results || [];

      // Filter out admin users using your exact API fields
      const filteredList = list.filter(
        (u) => !u.is_admin && !u.is_superuser && !u.is_staff
      );

      setUsers(filteredList);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };
  fetchUsers();
}, []);

  const loadPermissions = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingPerms(true);
    setSaveSuccess(false);
    try {
      const data = await getUserPermissions(userId);
      setSelectedPages(data?.allowed_pages || []);
    } catch {
      toast.error("Failed to load user permissions");
      setSelectedPages([]);
    } finally {
      setLoadingPerms(false);
    }
  }, []);

  const handleUserChange = (e) => {
    const id = e.target.value;
    setSelectedUserId(id);
    loadPermissions(id);
  };

  const togglePage = (key) => {
    setSelectedPages((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
    setSaveSuccess(false);
  };

  const toggleSection = (section) => {
    const keys = section.pages.map((p) => p.key);
    const allChecked = sectionAllChecked(section, selectedPages);
    if (allChecked) {
      setSelectedPages((prev) => prev.filter((k) => !keys.includes(k)));
    } else {
      setSelectedPages((prev) => [...new Set([...prev, ...keys])]);
    }
    setSaveSuccess(false);
  };

  const selectAll = () => {
    setSelectedPages([...ALL_PAGE_KEYS]);
    setSaveSuccess(false);
  };

  const deselectAll = () => {
    setSelectedPages([]);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      toast.warning("Please select a user first");
      return;
    }
    setSaving(true);
    try {
      await setUserPermissions(selectedUserId, selectedPages);
      setSaveSuccess(true);
      toast.success("Permissions saved successfully!");
    } catch {
      toast.error("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto" }}>
      {/* ── Header Toolbar ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1.5px solid ${T.border}`,
          overflow: "hidden",
          mb: 1,
          mt: 1,
          background: `linear-gradient(135deg, ${T.primary}06 0%, ${T.white} 50%, ${T.primaryLight}15 100%)`,
        }}
      >
        <Box sx={{ px: 2, py: 0.75 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={2.5}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    ...sq,
                    background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: `0 2px 8px ${T.primary}35`,
                  }}
                >
                  <SecurityIcon sx={{ color: T.white, fontSize: 17 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.88rem",
                      color: T.text,
                      lineHeight: 1.15,
                    }}
                  >
                    User Access
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.62rem", color: T.textMuted, lineHeight: 1 }}
                  >
                    Manage page permissions
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4.5}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: "0.75rem" }}>Select User</InputLabel>
                <Select
                  value={selectedUserId}
                  label="Select User"
                  onChange={handleUserChange}
                  disabled={loadingUsers}
                  sx={{
                    ...sq,
                    bgcolor: T.white,
                    fontSize: "0.78rem",
                    height: 34,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: `1.5px solid ${T.border}`,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: T.primary,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: `2px solid ${T.primary}`,
                    },
                  }}
                  renderValue={(val) => {
                    const u = users.find(
                      (x) => String(x.id || x.user_id) === String(val)
                    );
                    return u ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            ...sq,
                            bgcolor: T.primaryLight,
                            color: T.primary,
                            fontSize: "0.5rem",
                            fontWeight: 800,
                            border: `1px solid ${T.border}`,
                          }}
                        >
                          {getInitials(u.name || u.email)}
                        </Avatar>
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                          {u.name || u.email}
                        </Typography>
                      </Stack>
                    ) : (
                      "Select User"
                    );
                  }}
                >
                  {loadingUsers ? (
                    <MenuItem disabled sx={{ fontSize: "0.75rem" }}>
                      Loading...
                    </MenuItem>
                  ) : (
                    users.map((u) => (
                      <MenuItem
                        key={u.id || u.user_id}
                        value={u.id || u.user_id}
                        sx={{ fontSize: "0.75rem", p: 0 }}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Avatar
                            sx={{
                              width: 18,
                              height: 18,
                              ...sq,
                              bgcolor: T.primaryLight,
                              color: T.primary,
                              fontSize: "0.5rem",
                              fontWeight: 800,
                              border: `1px solid ${T.border}`,
                            }}
                          >
                            {getInitials(u.name || u.email)}
                          </Avatar>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {u.name || u.email}
                            </Typography>
                           
                          </Box>
                        </Stack>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={selectAll}
                  disabled={!selectedUserId}
                  sx={{ ...ghostBtn, height: 34, px: 1.5 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={deselectAll}
                  disabled={!selectedUserId}
                  sx={{ ...dangerBtn, height: 34, px: 1.5 }}
                >
                  Clear All
                </Button>
                <Button
                  size="small"
                  onClick={handleSave}
                  disabled={saving || !selectedUserId}
                  startIcon={
                    saving ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <SaveIcon sx={{ fontSize: 14 }} />
                    )
                  }
                  sx={{ ...primaryBtn, height: 34, px: 2 }}
                >
                  {saving ? "Saving..." : "Save Permissions"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        
      </Paper>

      {!selectedUserId && (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            border: `1.5px dashed ${T.border}`,
            bgcolor: T.white,
            ...sq,
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              ...sq,
              bgcolor: T.primaryLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1,
            }}
          >
            <PersonIcon sx={{ fontSize: 26, color: T.primary }} />
          </Box>
          <Typography
            sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem", mb: 0.3 }}
          >
            Select a User
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: T.textMuted }}>
            Choose a user from the dropdown to manage their page access
          </Typography>
        </Paper>
      )}

      {selectedUserId && loadingPerms && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 8,
            gap: 1,
          }}
        >
          <CircularProgress sx={{ color: T.primary }} size={30} />
          <Typography sx={{ fontSize: "0.75rem", color: T.textMuted }}>
            Loading permissions...
          </Typography>
        </Box>
      )}

      {selectedUserId && !loadingPerms && (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(79,70,229,0.06)",
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              display: "flex",
              bgcolor: T.headerBg,
              borderBottom: `1.5px solid ${T.border}`,
              px: 0.75,
              py: 0.4,
            }}
          >
            <Typography
              sx={{
                width: 160,
                flexShrink: 0,
                fontSize: "0.78rem",
                fontWeight: 800,
                color: T.headerText,
                pl: 0.75,
              }}
            >
              Section
            </Typography>
            <Typography
              sx={{
                flex: 1,
                fontSize: "0.78rem",
                fontWeight: 800,
                color: T.headerText,
              }}
            >
              Pages
            </Typography>
          </Box>

          {/* Table Rows */}
          {PAGE_SECTIONS.map((section, idx) => {
            const colors =
              SECTION_COLORS[section.sectionKey] || SECTION_COLORS.dashboard;
            const allChecked = sectionAllChecked(section, selectedPages);
            const indeterminate = sectionIndeterminate(section, selectedPages);
            const isEven = idx % 2 === 0;

            return (
              <Box
                key={section.sectionKey}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  borderBottom:
                    idx < PAGE_SECTIONS.length - 1
                      ? `1px solid ${T.border}`
                      : "none",
                  bgcolor: isEven ? T.white : "#fafbff",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: colors.bg,
                    boxShadow: `inset 4px 0 0 ${colors.color}`,
                  },
                }}
              >
                {/* Section Label */}
                <Box
                  onClick={() => toggleSection(section)}
                  sx={{
                    width: 160,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.35,
                    px: 0.5,
                    py: 0.6,
                    cursor: "pointer",
                    borderRight: `1.5px solid ${T.border}`,
                    bgcolor: allChecked ? colors.bg : "transparent",
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: colors.light },
                  }}
                >
                  <Checkbox
                    checked={allChecked}
                    indeterminate={indeterminate}
                    onChange={() => toggleSection(section)}
                    onClick={(e) => e.stopPropagation()}
                    sx={sectionCheckbox(colors.color, allChecked)}
                  />
                  <Box
                    sx={{
                      width: 4,
                      height: "60%",
                      minHeight: 16,
                      borderRadius: 2,
                      bgcolor: allChecked ? colors.color : "transparent",
                      transition: "all 0.2s",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: allChecked ? 800 : 600,
                      color: allChecked ? colors.color : T.text,
                      lineHeight: 1.2,
                      transition: "all 0.2s",
                    }}
                  >
                    {section.section}
                  </Typography>
                </Box>

                {/* Page Checkboxes */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0,
                    px: 0.5,
                    py: 0.3,
                  }}
                >
                  {section.pages.map((page) => {
                    const isChecked = selectedPages.includes(page.key);
                    return (
                      <Box
                        key={page.key}
                        onClick={() => togglePage(page.key)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.15,
                          py: 0.2,
                          px: 0.5,
                          cursor: "pointer",
                          ...sq,
                          minWidth: 130,
                          transition: "all 0.2s",
                          border: "1.5px solid transparent",
                          bgcolor: isChecked ? colors.bg : "transparent",
                          borderColor: isChecked ? colors.border : "transparent",
                          "&:hover": {
                            bgcolor: colors.light,
                            borderColor: colors.border,
                            transform: "translateY(-1px)",
                            boxShadow: `0 2px 6px ${colors.glow}`,
                          },
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => togglePage(page.key)}
                          onClick={(e) => e.stopPropagation()}
                          sx={highlightedCheckbox(colors.color, isChecked)}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: isChecked ? colors.color : T.textSub,
                            fontWeight: isChecked ? 700 : 450,
                            lineHeight: 1.2,
                            transition: "all 0.2s",
                          }}
                        >
                          {page.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
}